import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, sectionName: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const sectionName = this.props.section || 'Section';
    this.setState({ hasError: true, error, sectionName });
    // Only log errors in development, and suppress errors on public pages
    const isPublicPage = typeof window !== 'undefined' && (
      window.location.pathname === '/' || 
      window.location.pathname === '/login' || 
      window.location.pathname === '/register'
    );
    
    if (process.env.NODE_ENV === 'development' && !isPublicPage) {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught render error:', error?.name, info?.componentStack);
    }
    
    // Only report errors if not on public pages
    if (typeof this.props.onError === 'function' && !isPublicPage) {
      this.props.onError(error, info);
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null, sectionName: '' });
  };

  render() {
    // hidden: the section is not part of the caller's role; render nothing.
    if (this.props.hidden) return null;
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback({ resetErrorBoundary: this.resetErrorBoundary });
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Support section and compact props for better error messages
      const sectionName = this.state.sectionName || this.props.section || 'Section';
      const isCompact = this.props.compact;
      
      if (isCompact) {
        return <span className="text-red-600 text-xs">{sectionName} failed to render</span>;
      }

      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                {sectionName} Error
              </h3>
              <p className="text-sm text-red-700 mt-1">
                {sectionName} failed to render
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

